import { useContext, useEffect, useState } from "react"
import polyline from "@mapbox/polyline"
import { XmppContext } from "@/stream/xmppClient"
import AdHocCommands from "@/api/adHocCommands"
import RoutePolylinesParser from "@/lib/routePolylinesPareser"
import type { RouteStop } from "@/reducers/routes2gSlice"

export type RouteStopPoint = {
  lat: number
  lng: number
  action?: string
  label?: string
}

function numeric(v: unknown): number | null {
  if (typeof v === "number" && !Number.isNaN(v)) return v
  if (typeof v === "string") {
    const n = parseFloat(v)
    return Number.isNaN(n) ? null : n
  }
  return null
}

function extractStops(stops: (RouteStop | undefined)[]): RouteStopPoint[] {
  const out: RouteStopPoint[] = []
  for (const stop of stops) {
    const loc = stop?.location as Record<string, unknown> | undefined
    if (!loc) continue
    const lat = numeric(loc.lat)
    const lng = numeric(loc.lon ?? loc.lng)
    if (lat === null || lng === null) continue
    out.push({
      lat,
      lng,
      action: stop?.action,
      label: (loc.companyName as string) || (loc.formattedAddress as string),
    })
  }
  return out
}

/**
 * Fetch the driver's route as it looked at a given moment and decode it into
 * MapLibre `[lng, lat]` coordinates + stops. Backed by the gps
 * `get_route_polylines` ad-hoc command (same `wizard@<vhost>/gps` service as
 * the fuel report), which returns the route version active at `timestamp`.
 */
export function useDriverRouteAtTime(
  driverJid: string,
  timestamp: string,
  enabled: boolean,
): {
  coordinates: [number, number][]
  stops: RouteStopPoint[]
  isLoading: boolean
} {
  const { client } = useContext(XmppContext)
  const [coordinates, setCoordinates] = useState<[number, number][]>([])
  const [stops, setStops] = useState<RouteStopPoint[]>([])
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    let cancelled = false
    if (!client || !enabled || !timestamp) {
      setCoordinates([])
      setStops([])
      return
    }
    setIsLoading(true)
    // full_route: keep the planned geometry from its origin — the default
    // trims the first leg to the driver's position at `timestamp`, which
    // regularly cuts off the beginning of the route on the detail map.
    AdHocCommands.getRoutePolylines(client, driverJid, timestamp, true)
      .then((result) => {
        const route = RoutePolylinesParser.parse(result)?.route
        const firstRoute = route?.routes?.[0]
        const legs = firstRoute?.legs ?? []
        const coords: [number, number][] = []
        for (const leg of legs) {
          if (!leg?.polyline) continue
          try {
            for (const [lat, lng] of polyline.decode(leg.polyline)) {
              coords.push([lng, lat])
            }
          } catch {
            // skip an unparseable leg polyline
          }
        }
        const rawStops = firstRoute?.stops ?? legs.map((leg) => leg?.stop)
        if (!cancelled) {
          setCoordinates(coords)
          setStops(extractStops(rawStops))
        }
      })
      .catch(() => {
        if (!cancelled) {
          setCoordinates([])
          setStops([])
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [client, driverJid, timestamp, enabled])

  return { coordinates, stops, isLoading }
}
